import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "row" }
const _hoisted_2 = { class: "col" }
const _hoisted_3 = {
  key: 0,
  class: "row"
}
const _hoisted_4 = {
  key: 1,
  class: "table"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("thead", null, [
  /*#__PURE__*/_createElementVNode("tr", null, [
    /*#__PURE__*/_createElementVNode("th", {
      scope: "col",
      style: {"width":"50%"}
    }, "Product name"),
    /*#__PURE__*/_createElementVNode("th", { scope: "col" })
  ])
], -1)
const _hoisted_7 = { scope: "row" }
const _hoisted_8 = { class: "font-weight-bold" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = ["disabled", "onClick"]
const _hoisted_11 = { key: 1 }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { key: 0 }
const _hoisted_14 = /*#__PURE__*/_createElementVNode("div", {
  class: "spinner-border spinner-border-sm text-light",
  role: "status"
}, [
  /*#__PURE__*/_createElementVNode("span", { class: "sr-only" }, "Processing")
], -1)

import { EVENT_NAME__FIX_UNCATEGORIZED_PRODUCT_CATEGORY_COMMAND_FINISHED } from '@/api/shopping/silpo/ProductsApi'
import _ from 'underscore'
import { CACHE_TTL__ONE_DAY } from '@/services/CacheService'
import { computed, onBeforeMount, reactive, ref, watch } from 'vue'
import { Category, CategoryTreeItem, ListResponse, Product } from '@/types/api'
import { FormKitSchemaNode } from '@formkit/core'
import { FormKitSelectOption } from '@/types/app.tmp'
import { cacheService } from '@/di/services'
import { silpoCategoriesApi, silpoProductsApi } from '@/di/api'
import eventBus from '@/di/eventBus'
import AppLoadingMessage from "@/components/app/AppLoadingMessage.vue";

interface Model {
  lagerId: number | null,
  categoryIdFstLevel: number | null,
  categoryIdSndLevel: number | null,
  categoryIdThrdLevel: number | null,
}

interface Lock {
  initialLoading: boolean,
  showFormLagerId: number | null,
  formSubmitting: boolean,
}

interface Data {
  categoryTree: CategoryTreeItem[] | null,
  categories: Category[],
  uncategorizedProducts: ListResponse<Product>,
  model: Model
}

interface RecentCategory {
  name: string,
  model: Omit<Model, 'lagerId'>
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SilpoManualFixUncategorizedProducts',
  setup(__props) {

const STORE_KEY_MANUAL_FIX_UNCATEGORIZED_PRODUCTS_RECENT_USED = '01FJ2TP52ZDW2ZHMT2T3RRAGD5'

const defaultModel = (lagerId: number | null = null): Model => ({
  lagerId,
  categoryIdFstLevel: null,
  categoryIdSndLevel: null,
  categoryIdThrdLevel: null
})

const lock = reactive<Lock>({ initialLoading: false, showFormLagerId: null, formSubmitting: false })
const data = reactive<Data>({
  categoryTree: null,
  categories: [],
  uncategorizedProducts: { items: [], total_count: 0 },
  model: defaultModel()
})
const model = ref<Model>(defaultModel())

watch(() => model.value.lagerId, (): void => {
  model.value.categoryIdFstLevel = categories.value.filter(({ parent_id }) => parent_id === null)[0]?.id
})

watch(() => model.value.categoryIdFstLevel, (id): void => {
  model.value.categoryIdSndLevel = categories.value.filter(({ parent_id }) => parent_id === id)[0]?.id || null
})

watch(() => model.value.categoryIdSndLevel, (id): void => {
  model.value.categoryIdThrdLevel = categories.value.filter(({ parent_id }) => parent_id === id)[0]?.id || null
})

const schema = computed<FormKitSchemaNode[]>((): FormKitSchemaNode[] => {
  const options = [
    {
      $formkit: 'hidden',
      name: 'lagerId'
    },
    {
      $formkit: 'select',
      name: 'categoryIdFstLevel',
      options: makeCategoryOptions(data.categoryTree as CategoryTreeItem[])
    }
  ]

  if (model.value.categoryIdFstLevel) {
    const categoryFirstLevel = data.categoryTree?.filter(({ id }) => parseInt('' + model.value.categoryIdFstLevel) === id)[0]

    if (
        categoryFirstLevel
        && categoryFirstLevel.children.length > 0
    ) {
      options.push({
        $formkit: 'select',
        name: 'categoryIdSndLevel',
        options: makeCategoryOptions(categoryFirstLevel.children)
      })

      if (model.value.categoryIdSndLevel) {
        const categorySndLevel = categoryFirstLevel.children.filter(({ id }) => parseInt('' + model.value.categoryIdSndLevel) === id)[0]

        if (
            categorySndLevel
            && categorySndLevel.children.length > 0
        ) {
          options.push({
            $formkit: 'select',
            name: 'categoryIdThrdLevel',
            options: makeCategoryOptions(categorySndLevel.children)
          })
        }
      }
    }
  }

  return options.map((item) => ({...item, outerClass: 'mb-3'}))
})

const selectedCategoryId = computed<number | null>((): number | null => {
  // @todo Simplify
  return getCategoryIdFromModel(model.value)
})

const selectedCategoryName = computed<string | null>((): string | null => {
  return getCategoryNameById(selectedCategoryId.value as number)
})

const categories = computed<Category[]>((): Category[] => {
  const makeCategoriesFromCategoryTree = (cti: CategoryTreeItem): Category[] => {
    const r: Category[] = [{ id: cti.id, name: cti.name, parent_id: cti.parent_id }]

    r.push(..._.union(...cti.children.map(makeCategoriesFromCategoryTree)))

    return r
  }

  return _.union(...data.categoryTree?.map(makeCategoriesFromCategoryTree) || [])
})

function makeCategoryOptions (categoryTree: CategoryTreeItem[]) {
  const options: FormKitSelectOption[] = []

  categoryTree.forEach(({ id, name }) => {
    options.push({ label: name, value: id })
  })

  return options
}

async function loadUncategorizedProducts (): Promise<void> {
  data.uncategorizedProducts = { items: [], total_count: 0 }
  data.uncategorizedProducts = await silpoProductsApi.getProducts({ filter: { category_id: 0 } })
}

function showFormForLager (lagerId: number): void {
  lock.showFormLagerId = lagerId
  model.value = defaultModel(lagerId)
}

async function submitForm (): Promise<void> {
  lock.formSubmitting = true

  const cleanUp = (): void => {
    lock.showFormLagerId = null
    model.value = defaultModel()
    lock.formSubmitting = false
  }

  eventBus.$once(EVENT_NAME__FIX_UNCATEGORIZED_PRODUCT_CATEGORY_COMMAND_FINISHED, async (): Promise<void> => {
    recentCategories.add(model.value)
    cleanUp()
    await loadUncategorizedProducts()
  })

  try {
    await silpoProductsApi.updateProductCategory({
      lagerId: model.value.lagerId as number,
      categoryId: parseInt('' + selectedCategoryId.value)
    })
  } catch (e) {
    cleanUp()
    console.error(e)
  }
}

function getCategoryIdFromModel (m: Model): number | null {
  return m.categoryIdThrdLevel || m.categoryIdSndLevel || m.categoryIdFstLevel
}

function setModelFromResent (m: Model) {
  model.value = { ...m, lagerId: model.value.lagerId as number }
}

function getCategoryNameById (id: number): string {
  return categories.value.filter((c) => id === c.id)[0].name
}

const recentCategories = {
  get (): RecentCategory[] {
    return _.chain(cacheService.get(STORE_KEY_MANUAL_FIX_UNCATEGORIZED_PRODUCTS_RECENT_USED) || [])
        .map((ids: number[]): Omit<Model, 'lagerId'> => ({
          categoryIdFstLevel: ids[0] || null,
          categoryIdSndLevel: ids[1] || null,
          categoryIdThrdLevel: ids[2] || null
        }))
        .reverse()
        .value()
        .map((m: Model): RecentCategory => ({
          name: getCategoryNameById(getCategoryIdFromModel(m) as number),
          model: m
        }))
  },
  add (m: Omit<Model, 'lagerId'>): void {
    cacheService.set(
        STORE_KEY_MANUAL_FIX_UNCATEGORIZED_PRODUCTS_RECENT_USED,
        _.chain(cacheService.get(STORE_KEY_MANUAL_FIX_UNCATEGORIZED_PRODUCTS_RECENT_USED) || [])
            .union([[
              m.categoryIdFstLevel,
              m.categoryIdSndLevel,
              m.categoryIdThrdLevel
            ].filter((t) => !!t)])
            .uniq(false, (d: []) => d.join('-'))
            .last(10)
            .value(),
        CACHE_TTL__ONE_DAY
    )
  }
}

onBeforeMount(async (): Promise<void> => {
  lock.initialLoading = true

  try {
    await Promise.all([
      silpoCategoriesApi.getCategoryTree()
          .then((list) => {
            data.categoryTree = list.items
          }),
      await loadUncategorizedProducts()
    ])
  } finally {
    lock.initialLoading = false
  }
})

return (_ctx: any,_cache: any) => {
  const _component_FormKitSchema = _resolveComponent("FormKitSchema")!
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (lock.initialLoading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(AppLoadingMessage, null, {
              default: _withCtx(() => [
                _createTextVNode("Loading")
              ]),
              _: 1
            })
          ]))
        : _createCommentVNode("", true),
      (data.uncategorizedProducts.items.length > 0 && !lock.initialLoading)
        ? (_openBlock(), _createElementBlock("table", _hoisted_4, [
            (data.uncategorizedProducts.items.length > 0)
              ? (_openBlock(), _createElementBlock("caption", _hoisted_5, " Total count of uncategorized products: " + _toDisplayString(data.uncategorizedProducts.total_count), 1))
              : _createCommentVNode("", true),
            _hoisted_6,
            _createElementVNode("tbody", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.uncategorizedProducts.items, (product, idx) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: `product-fix-${idx}`
                }, [
                  _createElementVNode("td", _hoisted_7, [
                    _createElementVNode("div", _hoisted_8, _toDisplayString(product.lager_name), 1),
                    (_unref(selectedCategoryId) && lock.showFormLagerId === product.vendor_code)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                          _createElementVNode("em", null, _toDisplayString(_unref(selectedCategoryName)), 1)
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("td", null, [
                    (lock.showFormLagerId !== product.vendor_code)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          type: "button",
                          class: "btn btn-primary btn-sm",
                          disabled: lock.formSubmitting,
                          onClick: ($event: any) => (showFormForLager(product.vendor_code))
                        }, " Fix ", 8, _hoisted_10))
                      : _createCommentVNode("", true),
                    (lock.showFormLagerId === product.vendor_code)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recentCategories.get(), (resent, resentIds) => {
                            return (_openBlock(), _createElementBlock("button", {
                              key: `resent-category-index-${resentIds}`,
                              type: "button",
                              class: "btn btn-sm btn-light",
                              onClick: ($event: any) => (setModelFromResent(resent.model))
                            }, _toDisplayString(resent.name), 9, _hoisted_12))
                          }), 128)),
                          _createVNode(_component_FormKit, {
                            modelValue: model.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((model).value = $event)),
                            type: "form",
                            actions: false,
                            onSubmit: submitForm
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_FormKitSchema, { schema: _unref(schema) }, null, 8, ["schema"]),
                              _createVNode(_component_FormKit, {
                                type: "submit",
                                "input-class": "btn btn-sm btn-primary"
                              }, {
                                default: _withCtx(() => [
                                  (lock.formSubmitting)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                        _hoisted_14,
                                        _createTextVNode(" Processing... ")
                                      ]))
                                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                        _createTextVNode("Fix")
                                      ], 64))
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }, 8, ["modelValue"])
                        ]))
                      : _createCommentVNode("", true)
                  ])
                ]))
              }), 128))
            ])
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})